// WbLiveWeightContext — isolated live-weight SSE feed.
//
// The on-prem agent streams a weight reading many times per second while a
// vehicle is on the bridge. Those high-frequency ticks used to live on
// WbTicketContext, so every reading re-rendered the entire ticket form tree
// (left rail, line editor, right rail). We split them out here: only the
// components that actually render the live readout (WbWeightDisplay) and the
// per-line capture buttons (WbTicketCentrePaneLines) subscribe to this
// context, so a weight tick no longer churns the editor.
//
// Deliberately tiny: just the latest reading + stream-active flag and their
// setters. No persistence, no autosave — that all stays on WbTicketContext.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

interface WbLiveWeightContextValue {
  liveWeightKg: number | null;
  liveWeightStable: boolean;
  weightStreamActive: boolean;
  setLiveWeight: (kg: number | null, stable: boolean) => void;
  setWeightStreamActive: (active: boolean) => void;
}

const WbLiveWeightContext = createContext<WbLiveWeightContextValue | null>(null);

export function WbLiveWeightProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [liveWeightKg, setLiveWeightKg] = useState<number | null>(null);
  const [liveWeightStable, setLiveWeightStable] = useState(false);
  const [weightStreamActive, setWeightStreamActive] = useState(false);

  const setLiveWeight = useCallback((kg: number | null, stable: boolean) => {
    setLiveWeightKg(kg);
    setLiveWeightStable(stable);
  }, []);

  const value = useMemo<WbLiveWeightContextValue>(
    () => ({
      liveWeightKg,
      liveWeightStable,
      weightStreamActive,
      setLiveWeight,
      setWeightStreamActive,
    }),
    [liveWeightKg, liveWeightStable, weightStreamActive, setLiveWeight],
  );

  return (
    <WbLiveWeightContext.Provider value={value}>
      {children}
    </WbLiveWeightContext.Provider>
  );
}

export function useWbLiveWeight(): WbLiveWeightContextValue {
  const value = useContext(WbLiveWeightContext);
  if (value === null) {
    throw new Error('useWbLiveWeight must be used within a WbLiveWeightProvider');
  }
  return value;
}
